'use client';

import { useEffect, useMemo, useState } from 'react';
import useSearch from '@/hooks/studbook/useSearch';
import { Button } from '@mui/material';
import {
  GridSortModel,
  GridColumnVisibilityModel,
  GridCallbackDetails,
  GridColumnOrderChangeParams,
  GridFilterModel,
  GridLogicOperator,
} from '@mui/x-data-grid-pro';
import WithSkeleton from '../../utils/WithSkeleton';
import useGridStore from '@/studbook/store/useGridStore';
import useStudbookContext from '@/studbook/store/useStudbookContext';
import PublicSearchDataGrid from './PublicSearchDataGrid';
import useMemberFilterStore from '@/studbook/store/useMemberFilterStore';
import { debounce } from 'lodash';
import { ComparisonOperator } from '@/apiClient';
import { ComparisonOperatorText } from '@/studbook/constants';

interface Props {
  clearFilter: () => void;
  handleColumnVisibilityChange: (
    model: GridColumnVisibilityModel,
    details: GridCallbackDetails,
  ) => void;
  handleColumnOrderChange: (params: GridColumnOrderChangeParams) => void;
  handleSortModelChange: (model: GridSortModel) => void;
  orderedColumns?: any[];
}

export default function MemberSearchResult({
  clearFilter,
  handleColumnVisibilityChange,
  handleColumnOrderChange,
  handleSortModelChange,
  orderedColumns,
}: Props) {
  const {
    columnVisibilityModel,
    gridSortModel,
    paginationModel,
    setPaginationModel,
  } = useGridStore();
  const { orgId } = useStudbookContext();
  const { filters, setFilters } = useMemberFilterStore();
  const { data, previousData, loading, dataGrid } = useSearch(
    orgId,
    paginationModel,
  );
  const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const handleFilterModelChange = useMemo(
    () =>
      debounce(
        (model: GridFilterModel, details: GridCallbackDetails<'filter'>) => {
          if (!details.reason) return;
          setGridFilterModel(model);
          setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
          setFilters(
            model.items.map(({ field, operator, value }, index) => ({
              detailName:
                dataGrid.columns?.find((col) => col.field === field)
                  // @ts-ignore
                  ?.entityDetailName || '',
              id: `${index}:${field}`,
              logicalOperator:
                model.logicOperator === GridLogicOperator.Or ? 1 : 0,
              operator:
                ComparisonOperator[
                (operator[0].toUpperCase() +
                  operator.slice(1)) as keyof typeof ComparisonOperator
                ],
              value,
            })),
          );
        },
        500,
      ),
    [
      dataGrid.columns,
      paginationModel.pageSize,
      setFilters,
      setPaginationModel,
    ],
  );

  // 2 way binding between data grid filter model and animal filter store
  useEffect(() => {
    setGridFilterModel({
      items: filters.map(({ detailName, operator, value }, index) => {
        const field =
          // @ts-ignore
          dataGrid.columns?.find((col) => col.entityDetailName === detailName)
            ?.field || '';
        const operatorText =
          ComparisonOperatorText[
            operator as keyof typeof ComparisonOperatorText
          ].toLowerCase();
        return {
          field,
          id: `${index}:${field}`,
          operator: operatorText,
          value,
        };
      }),
      logicOperator: !filters.at(-1)?.logicalOperator
        ? GridLogicOperator.And
        : GridLogicOperator.Or,
    });
  }, [dataGrid.columns, filters]);

  return (
    <WithSkeleton isLoading={loading} useLinearProgress>
      <PublicSearchDataGrid
        columns={orderedColumns ?? [...(dataGrid.columns ?? [])]}
        columnVisibilityModel={columnVisibilityModel}
        filterDebounceMs={500}
        filterMode="server"
        filterModel={gridFilterModel}
        getRowId={dataGrid.getRowId}
        initialState={{
          columns: {
            columnVisibilityModel,
          },
        }}
        loading={loading}
        pagination
        paginationModel={paginationModel}
        rowCount={loading ? previousData?.rowCount ?? 0 : data?.rowCount ?? 0}
        rows={dataGrid.rows ?? []}
        slots={{
          noRowsOverlay: () => (
            <Button color="warning" onClick={() => clearFilter()}>
              Clear search
            </Button>
          ),
        }}
        sortModel={gridSortModel}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnVisibilityModelChange={handleColumnVisibilityChange}
        onFilterModelChange={handleFilterModelChange}
        onPaginationModelChange={setPaginationModel}
        onSortModelChange={handleSortModelChange}
      />
    </WithSkeleton>
  );
}
